/**
 * Application Header Component for Cedar Heights Music Academy Admin System
 * 
 * This component provides the main navigation header with:
 * - Logo and branding
 * - Role toggle for dual-role users (Admin/Teacher)
 * - Navigation menu
 * - Notifications badge
 * - User profile menu
 * - Mobile hamburger menu
 */

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Box,
  Avatar,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  SwapHoriz as SwapHorizIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';
import { UserRole } from '../../types';
import { useAuth } from '../../stores/authStore';
import { useTheme as useUITheme } from '../../stores/uiStore';

interface AppHeaderProps {
  userRole: UserRole;
  currentRole?: 'admin' | 'teacher';
  onRoleSwitch?: (role: 'admin' | 'teacher') => void;
  onMenuClick: () => void;
  sidebarOpen: boolean;
}

/**
 * Main application header with responsive design and role-based features
 */
export const AppHeader: React.FC<AppHeaderProps> = ({
  userRole,
  currentRole,
  onRoleSwitch,
  onMenuClick,
  sidebarOpen,
}) => {
  const theme = useTheme();
  const { logout, user } = useAuth();
  const { theme: currentTheme, toggleTheme } = useUITheme();
  
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Check if user has dual roles (admin + teacher) and currentRole is provided
  const isDualRole = userRole === 'admin' && currentRole && onRoleSwitch;

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleRoleSwitch = (role: 'admin' | 'teacher') => {
    onRoleSwitch?.(role);
    handleUserMenuClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleUserMenuClose();
    } catch (error) {
      console.error('Logout failed:', error);
      handleUserMenuClose();
    }
  };

  const handleThemeToggle = () => {
    toggleTheme();
  };

  return (
    <>
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: theme.shadows[1],
      }}
    >
      <Toolbar
        sx={{
          minHeight: {
            xs: 56,
            sm: 64,
          },
          paddingX: theme.spacing(2),
        }}
      >
        {/* Mobile Menu Button */}
        <IconButton
          color="inherit"
          aria-label="toggle sidebar"
          onClick={onMenuClick}
          edge="start"
          sx={{
            marginRight: theme.spacing(2),
            display: { md: sidebarOpen ? 'none' : 'flex' },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Avatar
            src="/logo.JPG"
            alt="Cedar Heights Music Academy"
            sx={{
              width: 40,
              height: 40,
              marginRight: theme.spacing(2),
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontWeight: 600,
            }}
          >
            Cedar Heights Music Academy
          </Typography>
        </Box>

        {/* Role Toggle Button (for dual-role users) */}
        {isDualRole && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<SwapHorizIcon />}
            onClick={() => handleRoleSwitch(currentRole === 'admin' ? 'teacher' : 'admin')}
            sx={{
              marginRight: theme.spacing(2),
              display: { xs: 'none', sm: 'flex' },
              textTransform: 'none',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            }}
          >
            {currentRole === 'admin' ? 'Switch to Teacher' : 'Switch to Admin'}
          </Button>
        )}

        {/* Dark Mode Toggle */}
        <IconButton
          color="inherit"
          aria-label="toggle theme"
          onClick={handleThemeToggle}
          sx={{ marginRight: theme.spacing(1) }}
        >
          {currentTheme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>

        {/* User Profile Menu */}
        <IconButton
          color="inherit"
          aria-label="user menu"
          onClick={handleUserMenuClick}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            <AccountCircleIcon />
          </Avatar>
        </IconButton>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchorEl}
          open={Boolean(userMenuAnchorEl)}
          onClose={handleUserMenuClose}
          PaperProps={{
            sx: {
              width: 200,
            },
          }}
        >
          {isDualRole && (
            <>
              <MenuItem onClick={() => handleRoleSwitch('admin')}>
                <Typography variant="body2">
                  {currentRole === 'admin' ? '✓ ' : ''}Admin Mode
                </Typography>
              </MenuItem>
              <MenuItem onClick={() => handleRoleSwitch('teacher')}>
                <Typography variant="body2">
                  {currentRole === 'teacher' ? '✓ ' : ''}Teacher Mode
                </Typography>
              </MenuItem>
            </>
          )}
          <Divider />
          <MenuItem onClick={handleLogout}>
            <Typography variant="body2" color="error">
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  </>
  );
};

export default AppHeader;
